import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  AppBar,
  Toolbar,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Rating,
  Link,
  useMediaQuery,
  Divider,
  Drawer,
  List as MuiList,
  ListItemButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Chip
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { styled } from '@mui/system';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { motion, AnimatePresence } from 'framer-motion';
import 'fontsource-inter';
import 'fontsource-roboto';
import {
  CleaningServices as CleaningServicesIcon,
  Home as HomeIcon,
  Build as BuildIcon,
  AcUnit as AcUnitIcon,
  Grass as GrassIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  HomeRepairService as HomeRepairServiceIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import Impressum from './Impressum';

const companyData = {
  "company": {
    "name": "SAMUSEW Gebäude & Garten Management",
    "owner": "Nikolai Samusew",
    "services": "Gebäude & Garten Management",
    "address": "Pfalzgrafenstraße 26, 67061 Ludwigshafen am Rhein",
    "contact": {
      "phone": "0176 313 39 183",
      "email": "info@samusew-clean-life.de",
      "domains": [
        "https://samusew-clean-life.de",
        "https://www.samusew-clean-life.de"
      ],
      "whatsapp": "https://wa.me/4917631339183",
      "facebook": "https://www.facebook.com/profile.php?id=61554667948715",
      "instagram": "https://www.instagram.com/p/C4itk0MMYW0/"
    },
    "insurance": {
      "name": "Nürnbergerversicherung",
      "address": "Marthastraße 16, 90482 Nürnberg"
    }
  }
};

const updateImages = {
  navIcon: 'clean_logo_header.webp',
  logo: 'update_images/newlogo.png',
  hero: 'hero-clean-life.jpg',
  video: 'update_images/VIDEO-2026-03-13-11-22-16.mp4',
  emblem: 'update_images/emblem.jpg',
  geschaeftsfuehrer: 'update_images/signatur.jpg',
  team: [
    'update_images/team.jpg',
    'update_images/team2.jpg',
    'update_images/team3.jpg'
  ],
  insta: [
    'update_images/insta_1.jpg',
    'update_images/insta_2.jpg',
  ]
};

const GREEN = '#3d8b40';
const GREEN_DARK = '#2e6b30';
const GREEN_LIGHT = '#eaf4ea';

const theme = createTheme({
  palette: {
    primary: { main: GREEN },
    secondary: { main: GREEN },
    background: {
      default: '#f5f7f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#555f55',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    h1: {
      fontWeight: 800,
      lineHeight: 1.1,
      letterSpacing: '-1px',
    },
    h2: {
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.5px',
    },
    h3: {
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: { fontWeight: 600, lineHeight: 1.3 },
    h5: { fontWeight: 600, lineHeight: 1.4 },
    h6: { fontWeight: 600, lineHeight: 1.5, letterSpacing: '0.1px' },
    body1: { fontSize: '1rem', lineHeight: 1.75 },
    body2: { lineHeight: 1.65 },
  },
  shape: { borderRadius: 6 },
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '6px !important',
          border: '1px solid #e2e8f0',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: '0 0 12px 0' },
        }
      }
    }
  }
});

// ─── Styled Components ────────────────────────────────────────────────────────

const HeroSection = styled(Box)(({ theme, heroImage }) => ({
  minHeight: '100vh',
  backgroundImage: `url(${process.env.PUBLIC_URL}/images/${heroImage || updateImages.hero})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.45) 60%, rgba(15,50,20,0.6) 100%)',
    zIndex: 1,
  },
}));

const HeroContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  width: '100%',
  paddingTop: theme.spacing(14),
  paddingBottom: theme.spacing(10),
}));

const fieldStyle = {
  width: '100%',
  padding: '14px 16px',
  border: '2px solid #e8e8e8',
  borderRadius: 8,
  fontSize: '0.95rem',
  fontFamily: "'Inter', sans-serif",
  transition: 'border-color 0.3s',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.82rem',
  fontWeight: 600,
  color: '#374151',
  marginBottom: 8,
  letterSpacing: '0.01em',
};

const GlassNav = styled(AppBar)(({ scrolled }) => ({
  backgroundColor: scrolled ? 'rgba(8,8,8,0.92)' : 'transparent',
  backdropFilter: scrolled ? 'blur(20px)' : 'none',
  WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
  boxShadow: 'none',
  borderBottom: scrolled ? '1px solid rgba(255,255,255,0.06)' : 'none',
  transition: 'all 0.4s ease',
  zIndex: 100,
}));

const SectionLabel = styled(Chip)({
  backgroundColor: GREEN_LIGHT,
  color: GREEN_DARK,
  fontWeight: 600,
  fontSize: '0.8rem',
  letterSpacing: '0.5px',
  height: 32,
  borderRadius: 100,
  marginBottom: 16,
});

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 0),
  [theme.breakpoints.down('md')]: { padding: theme.spacing(8, 0) },
  [theme.breakpoints.down('sm')]: { padding: theme.spacing(6, 0) },
}));

const LightSection = styled(SectionContainer)({ backgroundColor: '#f5f7f5' });
const WhiteSection = styled(SectionContainer)({ backgroundColor: '#ffffff' });

const NavLink = styled(Button)({
  color: 'white',
  padding: '10px 18px',
  fontWeight: 500,
  letterSpacing: '0.2px',
  textTransform: 'none',
  fontSize: '0.92rem',
  borderRadius: 4,
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
});

const GreenButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${GREEN} 0%, ${GREEN_DARK} 100%)`,
  color: 'white',
  padding: '13px 32px',
  fontWeight: 600,
  letterSpacing: '0.3px',
  borderRadius: 6,
  textTransform: 'none',
  fontSize: '1rem',
  boxShadow: '0 4px 16px rgba(61,139,64,0.25)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: `linear-gradient(135deg, ${GREEN_DARK} 0%, #1e4d20 100%)`,
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(61,139,64,0.35)',
  },
}));

const OutlineButton = styled(Button)({
  color: 'white',
  padding: '12px 30px',
  fontWeight: 600,
  letterSpacing: '0.3px',
  borderRadius: 6,
  textTransform: 'none',
  fontSize: '1rem',
  border: '2px solid rgba(255,255,255,0.5)',
  backdropFilter: 'blur(8px)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderColor: 'white',
  },
});

const ModernCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 4,
  overflow: 'hidden',
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 4px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.06)',
  transition: 'transform 0.35s ease, box-shadow 0.35s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
  },
}));

const StatCard = styled(Paper)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(5, 3),
  borderRadius: 4,
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 4px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.06)',
  backgroundColor: 'white',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 16px 48px rgba(61,139,64,0.08)',
  },
}));

const TestimonialCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5, 4),
  borderRadius: 4,
  border: '1px solid #e2e8f0',
  backgroundColor: 'white',
  boxShadow: '0 1px 4px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.06)',
}));

const NavIconBtn = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  color: GREEN,
  width: 44,
  height: 44,
  '&:hover': {
    backgroundColor: GREEN,
    color: 'white',
    borderColor: GREEN,
  },
}));


const GalleryImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  transition: 'transform 0.5s ease',
});

const GalleryItem = styled(Box)(({ theme }) => ({
  borderRadius: 4,
  overflow: 'hidden',
  position: 'relative',
  cursor: 'pointer',
  '&:hover img': { transform: 'scale(1.06)' },
  '&:hover .gallery-overlay': { opacity: 1 },
}));

const GalleryOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(to top, rgba(46,107,48,0.65) 0%, transparent 60%)',
  opacity: 0,
  transition: 'opacity 0.4s ease',
  display: 'flex',
  alignItems: 'flex-end',
  padding: 20,
});

// ─── Component ────────────────────────────────────────────────────────────────

function LandingPage(props) {
  const mode = props?.mode || 'default';
  const isAktion = mode === 'aktion';
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentBeforeAfterIndex, setCurrentBeforeAfterIndex] = useState(0);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [expandedAccordion, setExpandedAccordion] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRef = useRef(null);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMobileMenuToggle = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const offset = isMobile ? -70 : -80;
      window.scrollTo({
        top: section.getBoundingClientRect().top + window.pageYOffset + offset,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  }, [isMobile]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const toggleVideo = () => {
    if (!videoRef.current) return;
    if (videoPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setVideoPlaying(prev => !prev);
  };

  const beforeAfterImages = [
    { before: 'vorher1.jpg', after: 'nachher1.jpg' },
    { before: 'vorher2.jpg', after: 'nachher2.jpg' },
    { before: 'vorher4.jpg', after: 'nachher4.jpg' },
  ];

  const trustImages = [
    { image: updateImages.team[0], title: 'Mitarbeiter im Einsatz', text: 'Unser Team ist direkt vor Ort und kümmert sich um gepflegte Außenanlagen.' },
    { image: updateImages.team[1], title: 'Echte Arbeit am Objekt', text: 'Wir zeigen laufende Arbeiten direkt am Objekt – keine anonymen Stockfotos.' },
    { image: updateImages.team[2], title: 'Persönliche Betreuung', text: 'Direkter Kontakt und familiäre Zusammenarbeit schaffen langfristiges Vertrauen.' },
  ];

  const testimonials = [
    {
      name: 'Anonym von MyHammer',
      service: 'Treppenhausreinigung, Gartenpflege',
      text: 'Absolut zuverlässig! Immer sehr sauber und pünktlich gereinigt. Gelbe und Blaue Tonne immer rausgestellt und reingeholt. Garten immer gut gepflegt. Werden Firma weiterempfehlen. Absolutes Top Preis-Leistungsverhältnis.',
      rating: 5,
    },
    {
      name: 'Anonym von MyHammer.de',
      service: '4 Räume, Wohnungsreinigung, Boden, Treppen, Badezimmer',
      text: 'Sehr zufrieden mit dem Ergebnis.',
      rating: 5,
    },
    {
      name: 'Katja von MyHammer.de',
      service: 'Wohnungsreinigung, Boden, Badezimmer, Fenster und Türen',
      text: 'Sehr sympathischer Geschäftsführer und Team. Die Arbeiten wurden wie vereinbart, pünktlich und ordentlich ausgeführt. Die Kommunikation war freundlich und zügig. Prima Preis-Leistungsverhältnis. Werde ich wieder beauftragen und weiterempfehlen.',
      rating: 5,
    },
    {
      name: 'Alexa Riehl von MyHammer.de',
      service: 'Wohnungsreinigung, Boden, Badezimmer, Fenster und Türen',
      text: 'Sehr netter und aufmerksamer Kontakt. Reinigung lief pünktlich und sehr gewissenhaft ab. Preis/Leistung absolut passend. Gerne immer wieder.',
      rating: 5,
    },
  ];

  const services = [
    { title: 'Gebäudereinigung', description: 'Von der Grund- bis zur Spezialreinigung – maßgeschneiderte Lösungen für jedes Bedürfnis.', image: 'leistung_image_reinigung.jpg' },
    { title: 'Winterdienst', description: 'Sicherheit durch professionelle Schnee- und Eisbeseitigung auf Wegen und Flächen.', image: 'schnee_schippen.jpg' },
    { title: 'Hausmeisterservice', description: 'Umfassender Hausmeisterservice – von der Wartung bis zur Instandhaltung Ihrer Immobilie.', image: 'hausmeister.jpg' },
    { title: 'Beratung & Schulung', description: 'Individuelle Beratung und fortlaufende Schulungen garantieren hohe Dienstleistungsqualität.', image: 'leistung_image_beratung.jpg' },
    { title: 'Ökologisch & Effizient', description: 'Engagement für Ökologie und Effizienz sichert eine nachhaltige Pflege Ihrer Immobilien.', image: 'leistung_image_oekologisch.jpg' },
  ];

  const serviceCategories = [
    {
      id: 'interior', title: 'Innere Gebäudereinigung', icon: <CleaningServicesIcon />,
      services: [
        { name: 'Büroreinigung', desc: 'Professionelle Sauberkeit für ein produktives Arbeitsumfeld.' },
        { name: 'Unterhaltsreinigung', desc: 'Regelmäßige Reinigung, um Ihre Räume stets glänzend zu halten.' },
        { name: 'Grundreinigung', desc: 'Intensive Reinigung für einen Neuanfang in allen Bereichen.' },
        { name: 'Sofa Reinigung', desc: 'Tiefenreinigung für Sofas – entfernt Staub und Flecken.' },
        { name: 'Bett Reinigung', desc: 'Für eine hygienische und allergenfreie Schlafumgebung.' },
        { name: 'Praxisreinigung', desc: 'Hygienische Sauberkeit für medizinische und therapeutische Einrichtungen.' },
        { name: 'Treppenhausreinigung', desc: 'Sorgt für ein einladendes und sauberes Treppenhaus.' },
      ]
    },
    {
      id: 'exterior', title: 'Äußere Gebäudereinigung und Spezialreinigung', icon: <HomeIcon />,
      services: [
        { name: 'Fassadenreinigung', desc: 'Erstrahlen Sie Ihre Fassade in neuem Glanz.' },
        { name: 'Fensterreinigung', desc: 'Klare Sicht durch professionell gereinigte Fenster.' },
        { name: 'Dachreinigung', desc: 'Befreit Ihr Dach von Moos, Schmutz und Verunreinigungen.' },
        { name: 'Dachrinnenreinigung', desc: 'Verhindert Verstopfungen durch Laub und Schmutz.' },
        { name: 'Terrassenreinigung', desc: 'Für eine saubere und einladende Außenfläche.' },
        { name: 'Stadionreinigung', desc: 'Großflächige Reinigung für Veranstaltungs- und Sportstätten.' },
      ]
    },
    {
      id: 'technical', title: 'Technische Dienstleistungen und Wartung', icon: <BuildIcon />,
      services: [
        { name: 'Abflussreinigung', desc: 'Effektive Lösungen gegen Verstopfungen und Gerüche.' },
        { name: 'Abflusskamera Befahrung', desc: 'Präzise Diagnose von Rohrleitungen und Abflüssen.' },
        { name: 'Kleine Instandhaltungsarbeiten', desc: 'Zuverlässige Reparaturen und Wartungen.' },
        { name: 'Kleine Malerarbeiten', desc: 'Auffrischung Ihrer Räume mit professionellem Anstrich.' },
      ]
    },
    {
      id: 'winter', title: 'Winterdienst', icon: <AcUnitIcon />,
      services: [
        { name: 'Winterdienst', desc: 'Sicherheit durch Schnee- und Eisbeseitigung auf Wegen und Flächen.' },
        { name: 'Streudienst', desc: 'Sicherer Gehweg durch rechtzeitiges Streuen.' },
        { name: 'Eisbeseitigung', desc: 'Entfernung von Eisbildung für maximale Sicherheit.' },
        { name: 'Straßenreinigung', desc: 'Saubere und gepflegte Straßen für ein schönes Stadtbild.' },
        { name: 'Rinnenkästen und Abflussschächte', desc: 'Fachgerechte Installation für effiziente Entwässerung.' },
      ]
    },
    {
      id: 'hausmeister', title: 'Hausmeisterservice', icon: <HomeRepairServiceIcon />,
      services: [
        { name: 'Hausmeisterservice', desc: 'Umfassender Service für die Betreuung Ihrer Immobilie.' },
        { name: 'Gebäudewartung', desc: 'Regelmäßige Wartung für langfristigen Werterhalt.' },
        { name: 'Mülltonnenservice', desc: 'Zuverlässiges Rausstellen und Reinholen der Mülltonnen.' },
        { name: 'Kleine Reparaturen', desc: 'Schnelle und zuverlässige Behebung kleinerer Schäden.' },
        { name: 'Schlüsselverwaltung', desc: 'Sichere Verwaltung und Übergabe von Schlüsseln.' },
        { name: 'Beleuchtungskontrolle', desc: 'Regelmäßige Kontrolle der Beleuchtungsanlagen.' },
      ]
    },
    {
      id: 'garden', title: 'Garten- und Landschaftsbau', icon: <GrassIcon />,
      services: [
        { name: 'Garten- und Landschaftsbau', desc: 'Gestaltung traumhafter Gartenlandschaften.' },
        { name: 'Pflasterarbeiten', desc: 'Hochwertige Weg- und Platzgestaltung.' },
        { name: 'Garten anlegen', desc: 'Professionelle Anlage Ihres Wunschgartens.' },
        { name: 'Gartenpflege', desc: 'Regelmäßige Pflege für einen gesunden Garten.' },
        { name: 'Randstein setzen', desc: 'Präzises Setzen von Randsteinen.' },
        { name: 'Baumschnitt bis 12m', desc: 'Fachkundiger Schnitt für Gesundheit und Ästhetik.' },
      ]
    }
  ];

  const stats = [
    { value: '231+', label: 'Zufriedene Kunden', icon: <PeopleIcon sx={{ fontSize: '2.5rem', color: '#3d8b40' }} /> },
    { value: '3+', label: 'Jahre Erfahrung', icon: <WorkIcon sx={{ fontSize: '2.5rem', color: '#3d8b40' }} /> },
    { value: '800+', label: 'Aufträge', icon: <CleaningServicesIcon sx={{ fontSize: '2.5rem', color: '#3d8b40' }} /> },
  ];

  return (
    <ThemeProvider theme={theme}>

      {/* ── Navigation ─────────────────────────────────────────────────────── */}
      <GlassNav position="fixed" scrolled={scrolled ? 1 : 0}>
        <Toolbar sx={{ py: { xs: 1, md: 1.5 }, px: { xs: 2, md: 4 } }}>
          <Box
            onClick={() => scrollToSection('home')}
            sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.5 }, cursor: 'pointer' }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/${updateImages.navIcon}`}
              alt="SAMUSEW"
              style={{ height: isMobile ? 40 : 48, width: 'auto', display: 'block' }}
              loading="eager"
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
              <Typography
                sx={{
                  color: 'white',
                  fontWeight: 800,
                  fontSize: { xs: '0.95rem', md: '1.15rem' },
                  letterSpacing: '0.3px',
                  lineHeight: 1.1,
                }}
              >
                SAMUSEW
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.65)',
                  fontWeight: 500,
                  fontSize: { xs: '0.62rem', md: '0.72rem' },
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  mt: 0.25,
                }}
              >
                Gebäude & Garten Management
              </Typography>
            </Box>
          </Box>

          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {isAktion
                ? [['Anfrage', 'contact']].map(([label, id]) => (
                    <NavLink key={id} onClick={() => scrollToSection(id)}>{label}</NavLink>
                  ))
                : [['Leistungen', 'services'], ['Über uns', 'about'], ['Team', 'team'], ['Kontakt', 'contact']].map(([label, id]) => (
                    <NavLink key={id} onClick={() => scrollToSection(id)}>{label}</NavLink>
                  ))}
              }
              {!isAktion && (
                <GreenButton onClick={() => scrollToSection('contact')} sx={{ ml: 2, py: 1, px: 3, fontSize: '0.9rem' }}>
                  Angebot anfragen
                </GreenButton>
              )}
            </Box>
          )}

          {isMobile && (
            <IconButton onClick={handleMobileMenuToggle} sx={{ color: 'white' }} aria-label="Menü">
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </GlassNav>

      {/* ── Mobile Drawer ──────────────────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        PaperProps={{ sx: { width: 280, backgroundColor: '#0a0a0a', color: 'white' } }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <img src={`${process.env.PUBLIC_URL}/images/${updateImages.navIcon}`} alt="SAMUSEW" style={{ height: 40, width: 'auto' }} />
            <IconButton onClick={handleMobileMenuToggle} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <MuiList disablePadding>
            {isAktion
              ? [['Anfrage', 'contact']].map(([label, id]) => (
                  <ListItemButton
                    key={id}
                    onClick={() => scrollToSection(id)}
                    sx={{ py: 2, borderBottom: '1px solid rgba(255,255,255,0.07)', borderRadius: 1 }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{label}</Typography>
                  </ListItemButton>
                ))
              : [['Leistungen', 'services'], ['Über uns', 'about'], ['Team', 'team'], ['Kontakt', 'contact']].map(([label, id]) => (
                  <ListItemButton
                    key={id}
                    onClick={() => scrollToSection(id)}
                    sx={{ py: 2, borderBottom: '1px solid rgba(255,255,255,0.07)', borderRadius: 1 }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{label}</Typography>
                  </ListItemButton>
                ))}
          </MuiList>
          <Box sx={{ mt: 4 }}>
            {!isAktion && (
              <GreenButton fullWidth onClick={() => scrollToSection('contact')}>
                Angebot anfragen
              </GreenButton>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <HeroSection id="home" heroImage={isAktion ? 'hero-clean-life.jpg' : undefined}>
        <HeroContent>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8} lg={7}>
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      backgroundColor: 'rgba(61,139,64,0.15)',
                      border: '1px solid rgba(61,139,64,0.35)',
                      borderRadius: 100,
                      px: 2,
                      py: 0.75,
                      mb: 3,
                    }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#eaf4ea' }} />
                    <Typography variant="body2" sx={{ color: '#a8d5a2', fontWeight: 500 }}>
                      Professionell & Zuverlässig seit 2023
                    </Typography>
                  </Box>

                  <Typography
                    variant="h1"
                    sx={{ fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.8rem' }, mb: 3, color: 'white' }}
                  >
                    Wir machen sauber
                    <br />
                    <Box component="span" sx={{ color: '#3d8b40' }}>Sie genießen!</Box>
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{ mb: 5, opacity: 0.85, fontSize: { xs: '1.05rem', md: '1.2rem' }, maxWidth: 560, lineHeight: 1.7 }}
                  >
                    Ihr Ansprechpartner für professionelles Gebäude- und Gartenmanagement im Rhein-Neckar-Gebiet.
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 5 }}>
                    {['Gebäudereinigung', 'Winterdienst', 'Hausmeisterservice', 'Gartenpflege'].map(tag => (
                      <Box
                        key={tag}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.75,
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          backdropFilter: 'blur(8px)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: 100, px: 2, py: 0.75,
                        }}
                      >
                        <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#3d8b40' }} />
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 500, fontSize: '0.85rem' }}>
                          {tag}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <GreenButton onClick={() => scrollToSection('services')} endIcon={<ArrowForwardIcon />}>
                      Unsere Leistungen
                    </GreenButton>
                    <OutlineButton onClick={() => scrollToSection('video')} startIcon={<PlayArrowIcon />}>
                      Video ansehen
                    </OutlineButton>
                  </Box>

                  <Box sx={{ mt: 5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {[
                      { href: `tel:${companyData.company.contact.phone}`, icon: <PhoneIcon />, label: 'Anrufen' },
                      { href: companyData.company.contact.whatsapp, icon: <WhatsAppIcon />, label: 'WhatsApp', target: '_blank' },
                      { href: `mailto:${companyData.company.contact.email}`, icon: <EmailIcon />, label: 'E-Mail' },
                      { href: companyData.company.contact.instagram, icon: <InstagramIcon />, label: 'Instagram', target: '_blank' },
                      { href: companyData.company.contact.facebook, icon: <FacebookIcon />, label: 'Facebook', target: '_blank' },
                    ].map(({ href, icon, label, target }) => (
                      <IconButton
                        key={label}
                        component="a"
                        href={href}
                        target={target}
                        aria-label={label}
                        sx={{
                          color: 'white',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          backdropFilter: 'blur(8px)',
                          border: '1px solid rgba(255,255,255,0.15)',
                          transition: 'all 0.3s ease',
                          '&:hover': { backgroundColor: '#eaf4ea', borderColor: '#3d8b40', transform: 'translateY(-3px)' },
                        }}
                      >
                        {icon}
                      </IconButton>
                    ))}
                  </Box>
                </motion.div>
              </Grid>
            </Grid>

            {/* Scroll indicator */}
            <Box sx={{ position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
              <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
                <IconButton onClick={() => scrollToSection('services')} sx={{ color: 'rgba(255,255,255,0.6)' }}>
                  <KeyboardArrowDownIcon sx={{ fontSize: 32 }} />
                </IconButton>
              </motion.div>
            </Box>
          </Container>
        </HeroContent>
      </HeroSection>

      {/* ── Stats Strip ────────────────────────────────────────────────────── */}
      <Box sx={{ backgroundColor: '#0f172a', py: { xs: 4, md: 5 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center">
            {stats.map((stat, i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, py: 1 }}>
                  {stat.icon}
                  <Box>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 800, lineHeight: 1 }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Services ───────────────────────────────────────────────────────── */}
      <LightSection id="services">
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <SectionLabel label="Leistungen" />
            <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
              Was wir für Sie tun
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 620, mx: 'auto', mt: 2 }}>
              Klare Prozesse, feste Ansprechpartner und saubere Ergebnisse für Ihre Immobilie.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {services.map((service, index) => (
              <Grid item key={index} xs={12} sm={6} md={index < 2 ? 6 : 4}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <ModernCard elevation={0}>
                    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                      <CardMedia
                        component="img"
                        height={isMobile ? 200 : 260}
                        image={`${process.env.PUBLIC_URL}/images/${service.image}`}
                        alt={service.title}
                        loading={index === 0 ? 'eager' : 'lazy'}
                        sx={{ transition: 'transform 0.5s ease', '&:hover': { transform: 'scale(1.04)' } }}
                      />
                    </Box>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                        {service.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {service.description}
                      </Typography>
                    </CardContent>
                  </ModernCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Service detail accordion */}
          <Box sx={{ mt: 10 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <SectionLabel label="Im Detail" />
              <Typography variant="h3" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Alle Dienstleistungen im Überblick
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {serviceCategories.map((category) => (
                <Accordion
                  key={category.id}
                  expanded={expandedAccordion === category.id}
                  onChange={handleAccordionChange(category.id)}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#3d8b40' }} />}
                    sx={{ px: { xs: 2, md: 3 }, minHeight: 64 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ color: '#3d8b40', display: 'flex' }}>{category.icon}</Box>
                      <Typography variant="h6" sx={{ fontSize: { xs: '0.95rem', md: '1.1rem' } }}>
                        {category.title}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: { xs: 2, md: 3 }, pb: 3 }}>
                    <Grid container spacing={2}>
                      {category.services.map((srv, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                            <Box sx={{
                              width: 8, height: 8, borderRadius: '50%',
                              backgroundColor: '#eaf4ea', flexShrink: 0, mt: 0.75
                            }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                                {srv.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                {srv.desc}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        </Container>
      </LightSection>

      {/* ── Video Section ──────────────────────────────────────────────────── */}
      <WhiteSection id="video">
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
                    backgroundColor: '#000',
                    aspectRatio: '9/16',
                    maxWidth: { xs: 340, sm: 380, md: '100%' },
                    mx: 'auto',
                  }}
                >
              <video
                ref={videoRef}
                src={`${process.env.PUBLIC_URL}/images/${updateImages.video}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                playsInline
                loop
                onEnded={() => setVideoPlaying(false)}
              />

              {/* Play/Pause overlay */}
              <Box
                onClick={toggleVideo}
                sx={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                  background: videoPlaying ? 'transparent' : 'rgba(0,0,0,0.35)',
                  transition: 'background 0.3s ease',
                  '&:hover .play-btn': { transform: 'scale(1.1)' },
                }}
              >
                {!videoPlaying && (
                  <Box
                    className="play-btn"
                    sx={{
                      width: 80, height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(46,107,48,0.92)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 8px 32px rgba(46,107,48,0.4)',
                      transition: 'transform 0.3s ease',
                    }}
                  >
                    <PlayArrowIcon sx={{ fontSize: 42, color: 'white', ml: 0.5 }} />
                  </Box>
                )}
              </Box>
            </Box>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={7}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <SectionLabel label="In Aktion" />
                <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 3 }}>
                  Sehen Sie uns bei der Arbeit
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.05rem' }}>
                  Echte Einsätze, echte Ergebnisse – kein Stockmaterial, kein Marketing-Sprech. So sieht professionelle Reinigung im Alltag aus.
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 4 }}>
                  {[
                    { title: 'Professionelle Ausrüstung', desc: 'Hochdruck- und Spezialgeräte für jede Oberfläche.' },
                    { title: 'Geschultes Personal', desc: 'Erfahrene Mitarbeiter mit klarer Qualitätskontrolle.' },
                    { title: 'Saubere Ergebnisse', desc: 'Sichtbarer Unterschied – jederzeit dokumentiert.' },
                  ].map((item) => (
                    <Box key={item.title} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: '50%',
                        backgroundColor: GREEN_LIGHT,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <CheckCircleOutlineIcon sx={{ color: GREEN, fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.25 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.desc}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                <GreenButton
                  onClick={toggleVideo}
                  startIcon={videoPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                >
                  {videoPlaying ? 'Video pausieren' : 'Video abspielen'}
                </GreenButton>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </WhiteSection>

      {/* ── About ──────────────────────────────────────────────────────────── */}
      <LightSection id="about">
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Box
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                    border: '1px solid #e8e8e8',
                    backgroundColor: '#ffffff',
                    p: { xs: 2, md: 3 },
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/${updateImages.geschaeftsfuehrer}`}
                    alt="Nikolai Samusew – Geschäftsführer"
                    style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }}
                    loading="lazy"
                  />
                </Box>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={7}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <SectionLabel label="Über uns" />
                <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 3 }}>
                  Vertrauen durch echte Menschen und echte Ergebnisse
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Als familiärer Betrieb zeigen wir echte Einsätze aus dem Alltag. Bei uns arbeiten Menschen, die Ihre Immobilie persönlich betreuen.
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Ob Hausverwaltung, Mehrfamilienhaus oder Gewerbeobjekt: Wir stehen für transparente Kommunikation und verlässliche Qualität.
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Von der Grundreinigung bis zur Pflege von Außenanlagen liefern wir sichtbare Ergebnisse, die Vertrauen schaffen.
                </Typography>

                <Box sx={{ display: 'flex', gap: 3, mt: 4, flexWrap: 'wrap' }}>
                  {[['✓', 'Versichert', 'Nürnberger Versicherung'], ['✓', 'Seit 2023', 'Nachweisliche Erfahrung'], ['✓', 'Persönlich', 'Direkter Kontakt']].map(([icon, title, sub]) => (
                    <Box key={title} sx={{ flex: '1 1 140px' }}>
                      <Box sx={{
                        width: 40, height: 40, borderRadius: '50%',
                        backgroundColor: '#eaf4ea', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mb: 1,
                      }}>
                        <Typography sx={{ color: '#3d8b40', fontWeight: 700 }}>{icon}</Typography>
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
                      <Typography variant="body2" color="text.secondary">{sub}</Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ mt: 5 }}>
                  <GreenButton onClick={() => scrollToSection('contact')} endIcon={<ArrowForwardIcon />}>
                    Kontakt aufnehmen
                  </GreenButton>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </LightSection>

      {/* ── Team ───────────────────────────────────────────────────────────── */}
      <WhiteSection id="team">
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <SectionLabel label="Team" />
            <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
              Team & Familie
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mt: 2 }}>
              Echte Menschen, echte Arbeit – familiäres Miteinander schafft Vertrauen bei Hausverwaltungen und Eigentümern.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {trustImages.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.12 }}
                >
                  <ModernCard elevation={0}>
                    <Box sx={{ overflow: 'hidden' }}>
                      <CardMedia
                        component="img"
                        image={`${process.env.PUBLIC_URL}/images/${item.image}`}
                        alt={item.title}
                        sx={{ height: 300, objectFit: 'cover', transition: 'transform 0.5s ease', '&:hover': { transform: 'scale(1.04)' } }}
                      />
                    </Box>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{item.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.text}</Typography>
                    </CardContent>
                  </ModernCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </WhiteSection>

      {/* ── Gallery ────────────────────────────────────────────────────────── */}
      {/* ── Instagram / Social ─────────────────────────────────────────────── */}
      <LightSection>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <SectionLabel label="Instagram" />
            <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
              Folgen Sie uns
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mt: 2 }}>
              Aktuelle Einsätze und Ergebnisse täglich auf Instagram.
            </Typography>
          </Box>

          {/* Two equal-size cards */}
          <Grid container spacing={3} justifyContent="center">
            {updateImages.insta.map((img, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                >
                  <Box
                    component="a"
                    href={companyData.company.contact.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'block',
                      borderRadius: 4,
                      overflow: 'hidden',
                      boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                      border: '1px solid #e2e8f0',
                      position: 'relative',
                      cursor: 'pointer',
                      '&:hover img': { transform: 'scale(1.04)' },
                      '&:hover .insta-overlay': { opacity: 1 },
                    }}
                  >
                    <Box sx={{ height: { xs: 300, sm: 380, md: 440 }, overflow: 'hidden' }}>
                      <img
                        src={`${process.env.PUBLIC_URL}/images/${img}`}
                        alt={`Instagram Beitrag ${i + 1}`}
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          transition: 'transform 0.5s ease',
                        }}
                      />
                    </Box>
                    {/* Hover overlay */}
                    <Box
                      className="insta-overlay"
                      sx={{
                        position: 'absolute', inset: 0,
                        background: 'rgba(46,107,48,0.55)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: 0, transition: 'opacity 0.35s ease',
                        flexDirection: 'column', gap: 1,
                      }}
                    >
                      <InstagramIcon sx={{ color: 'white', fontSize: 40 }} />
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 700, letterSpacing: 0.5 }}>
                        @clean_life_samusew
                      </Typography>
                    </Box>
                    {/* Instagram badge */}
                    <Box sx={{
                      position: 'absolute', top: 14, right: 14,
                      backgroundColor: 'rgba(0,0,0,0.45)',
                      backdropFilter: 'blur(6px)',
                      borderRadius: 100, px: 1.5, py: 0.5,
                      display: 'flex', alignItems: 'center', gap: 0.75,
                    }}>
                      <InstagramIcon sx={{ color: 'white', fontSize: 14 }} />
                      <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, fontSize: '0.72rem' }}>
                        Instagram
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <GreenButton
              component="a"
              href={companyData.company.contact.instagram}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<InstagramIcon />}
            >
              Profil auf Instagram öffnen
            </GreenButton>
          </Box>
        </Container>
      </LightSection>

      {/* ── Before / After ─────────────────────────────────────────────────── */}
      <WhiteSection>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
                      <img
                        src={`${process.env.PUBLIC_URL}/images/${beforeAfterImages[currentBeforeAfterIndex].before}`}
                        alt={`Vorher ${currentBeforeAfterIndex + 1}`}
                        style={{ width: '100%', height: isMobile ? 180 : 280, objectFit: 'cover', display: 'block' }}
                      />
                      <Box sx={{ p: 1.5, backgroundColor: '#f1f5f9', textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>
                          Vorher
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 8px 32px rgba(61,139,64,0.1)' }}>
                      <img
                        src={`${process.env.PUBLIC_URL}/images/${beforeAfterImages[currentBeforeAfterIndex].after}`}
                        alt={`Nachher ${currentBeforeAfterIndex + 1}`}
                        style={{ width: '100%', height: isMobile ? 180 : 280, objectFit: 'cover', display: 'block' }}
                      />
                      <Box sx={{ p: 1.5, backgroundColor: '#eaf4ea', textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#3d8b40', textTransform: 'uppercase', letterSpacing: 1 }}>
                          Nachher
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 3 }}>
                  <NavIconBtn onClick={() => setCurrentBeforeAfterIndex(p => (p - 1 + beforeAfterImages.length) % beforeAfterImages.length)}>
                    <KeyboardArrowLeftIcon />
                  </NavIconBtn>
                  {beforeAfterImages.map((_, i) => (
                    <Box
                      key={i}
                      onClick={() => setCurrentBeforeAfterIndex(i)}
                      sx={{
                        width: i === currentBeforeAfterIndex ? 28 : 8,
                        height: 8, borderRadius: 100,
                        backgroundColor: i === currentBeforeAfterIndex ? '' : '#e2e8f0',
                        cursor: 'pointer', transition: 'all 0.3s ease',
                        alignSelf: 'center',
                      }}
                    />
                  ))}
                  <NavIconBtn onClick={() => setCurrentBeforeAfterIndex(p => (p + 1) % beforeAfterImages.length)}>
                    <KeyboardArrowRightIcon />
                  </NavIconBtn>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <SectionLabel label="Vorher & Nachher" />
              <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 3 }}>
                Unsere Arbeit spricht für sich
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Echte Einsätze, echte Ergebnisse. Sehen Sie selbst, wie wir Objekte in einwandfreiem Zustand übergeben – transparent und ohne Retuschen.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 4, flexWrap: 'wrap' }}>
                <GreenButton onClick={() => scrollToSection('contact')} endIcon={<ArrowForwardIcon />}>
                  Angebot anfragen
                </GreenButton>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </WhiteSection>

      {/* ── Testimonials ───────────────────────────────────────────────────── */}
      <LightSection>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <SectionLabel label="Bewertungen" />
            <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
              Was unsere Kunden sagen
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Alle Bewertungen auf{' '}
              <Link href="https://www.my-hammer.de/auftragnehmer/clean-life-gebaeude-garten-management" target="_blank" sx={{ color: '#3d8b40', fontWeight: 600 }}>
                MyHammer.de
              </Link>
            </Typography>
          </Box>

          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTestimonialIndex}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.35 }}
              >
                <TestimonialCard elevation={0}>
                  <Box sx={{ mb: 3 }}>
                    <Rating
                      value={testimonials[currentTestimonialIndex].rating}
                      readOnly
                      icon={<StarIcon sx={{ color: '#3d8b40', fontSize: 22 }} />}
                      emptyIcon={<StarIcon sx={{ color: '#e2e8f0', fontSize: 22 }} />}
                    />
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#1e293b', fontStyle: 'italic', mb: 4 }}
                  >
                    „{testimonials[currentTestimonialIndex].text}"
                  </Typography>

                  <Divider sx={{ mb: 3 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {testimonials[currentTestimonialIndex].name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {testimonials[currentTestimonialIndex].service}
                      </Typography>
                    </Box>
                    <Chip label="Verifizierte Bewertung" size="small" sx={{ backgroundColor: '#eaf4ea', color: '#3d8b40', fontWeight: 600 }} />
                  </Box>
                </TestimonialCard>
              </motion.div>
            </AnimatePresence>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 4, alignItems: 'center' }}>
              <NavIconBtn onClick={() => setCurrentTestimonialIndex(p => (p - 1 + testimonials.length) % testimonials.length)}>
                <KeyboardArrowLeftIcon />
              </NavIconBtn>
              {testimonials.map((_, i) => (
                <Box
                  key={i}
                  onClick={() => setCurrentTestimonialIndex(i)}
                  sx={{
                    width: i === currentTestimonialIndex ? 28 : 8, height: 8,
                    borderRadius: 100,
                    backgroundColor: i === currentTestimonialIndex ? '' : '#e2e8f0',
                    cursor: 'pointer', transition: 'all 0.3s ease',
                  }}
                />
              ))}
              <NavIconBtn onClick={() => setCurrentTestimonialIndex(p => (p + 1) % testimonials.length)}>
                <KeyboardArrowRightIcon />
              </NavIconBtn>
            </Box>
          </Box>
        </Container>
      </LightSection>

      {isAktion ? (
        <WhiteSection id="anfrage">
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
              <Grid item xs={12} md={5}>
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                    <SectionLabel label="Aktion" />
                    <Typography variant="h2" sx={{ fontSize: { xs: '1.7rem', md: '2.3rem' }, mt: 1 }}>
                      Treppenhaus aufwerten
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                      1. Monat Treppenhauspflege inklusive. Ohne Risiko, ohne Verpflichtung.
                    </Typography>
                    <Box component="ul" sx={{ mt: 3, pl: { xs: 3, md: 0 }, textAlign: 'left' }}>
                      {['Individueller Reinigungsplan', 'Keine Mindestlaufzeit', 'Transparente Preise'].map(item => (
                        <Box component="li" key={item} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 0.6 }}>
                          <CheckCircleOutlineIcon sx={{ color: '#3d8b40' }} />
                          <Typography variant="body2">{item}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
                      *Gilt bei Auftragserteilung bis 31.07.2026.
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>

              <Grid item xs={12} md={7}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#ffffff',
                      borderRadius: 4,
                      p: { xs: 2.5, md: 4 },
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2.5, color: '#1f3d24', fontSize: { xs: '1.15rem', md: '1.35rem' } }}>
                      Kostenlose Besichtigung anfragen
                    </Typography>
                    <form id="contactForm" action="https://formspree.io/f/xykabewd" method="POST" style={{ display: 'grid', gap: { xs: 10, md: 14 } }}>
                      <input type="hidden" name="_subject" id="formSubject" value="Neue Anfrage von der Treppenhaus-Landingpage" />
                      <input type="hidden" name="_next" value="" />
                      <input type="text" name="aktionscode" placeholder="Aktionscode *" required style={fieldStyle} />
                      <input type="text" name="name" placeholder="Name *" required style={fieldStyle} />
                      <input type="email" name="email" placeholder="E-Mail *" required style={fieldStyle} />
                      <input type="tel" name="phone" placeholder="Telefon *" required style={fieldStyle} />
                      <select name="units" style={fieldStyle}>
                        <option value="">Anzahl Wohneinheiten</option>
                        <option value="1-5">1–5</option>
                        <option value="6-15">6–15</option>
                        <option value="16-30">16–30</option>
                        <option value="30+">Über 30</option>
                      </select>
                      <select name="service" id="serviceSelect" required style={fieldStyle}>
                        <option value="">Gewünschte Dienstleistung *</option>
                        <option value="Treppenhausreinigung">Treppenhausreinigung</option>
                        <option value="Fensterreinigung">Fensterreinigung</option>
                        <option value="Unterhaltsreinigung">Unterhaltsreinigung</option>
                        <option value="Büro Unterhaltsreinigung">Büro Unterhaltsreinigung</option>
                        <option value="Winterdienst">Winterdienst</option>
                        <option value="Hausmeisterdienste">Hausmeisterdienste</option>
                      </select>
                      <input
                        type="text"
                        name="weiterempfehlungscode"
                        placeholder="Weiterempfehlungscode (z.B. FREUNDE100)"
                        style={fieldStyle}
                      />
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <input
                          type="checkbox"
                          id="privacy"
                          name="privacy"
                          required
                          style={{ marginTop: 4, accentColor: '#3d8b40' }}
                        />
                        <label
                          htmlFor="privacy"
                          style={{ fontSize: '0.8rem', color: '#555f55', lineHeight: 1.5 }}
                        >
                          Ich stimme der Verarbeitung meiner Daten gemäß der{' '}
                          <Link href="/impressum" target="_blank" style={{ color: '#2e6b30', textDecoration: 'underline' }}>
                            Datenschutzerklärung
                          </Link>
                          zu. Meine Daten werden ausschließlich zur Bearbeitung meiner Anfrage verwendet.
                        </label>
                      </Box>
                      <Button
                        type="submit"
                        sx={{
                          background: 'linear-gradient(135deg, #3d8b40 0%, #2e6b30 100%)',
                          color: 'white',
                          py: 1.5,
                          fontWeight: 700,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #2e6b30 0%, #1e4d20 100%)',
                          },
                        }}
                      >
                        Besichtigung anfragen
                      </Button>
                    </form>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </WhiteSection>
      ) : (
        <WhiteSection id="contact">
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <SectionLabel label="Kontakt" />
              <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                Wir sind für Sie da
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto', mt: 2 }}>
                Haben Sie Fragen? Wir beraten Sie gerne persönlich – per Anruf, Mail oder WhatsApp.
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 8, md: 6 }}>
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 4,
                    p: 4,
                    height: '100%',
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 4 }}>
                    Kontaktinformationen
                  </Typography>

                  <List disablePadding>
                    {[
                      { icon: <LocationOnIcon sx={{ color: '#3d8b40' }} />, text: companyData.company.address },
                      { icon: <PhoneIcon sx={{ color: '#3d8b40' }} />, text: companyData.company.contact.phone, href: `tel:${companyData.company.contact.phone}` },
                      { icon: <EmailIcon sx={{ color: '#3d8b40' }} />, text: companyData.company.contact.email, href: `mailto:${companyData.company.contact.email}` },
                    ].map(({ icon, text, href }, i) => (
                      <ListItem key={i} disablePadding sx={{ py: 1.5, borderBottom: i < 2 ? '1px solid #e2e8f0' : 'none' }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
                        <ListItemText
                          primary={href
                            ? <Link href={href} color="inherit" underline="hover" sx={{ fontWeight: 500 }}>{text}</Link>
                            : <Typography variant="body2" sx={{ fontWeight: 500 }}>{text}</Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Box sx={{ mt: 4 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem' }}>
                      Social Media
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      {[
                        { href: companyData.company.contact.whatsapp, icon: <WhatsAppIcon />, label: 'WhatsApp' },
                        { href: companyData.company.contact.instagram, icon: <InstagramIcon />, label: 'Instagram' },
                        { href: companyData.company.contact.facebook, icon: <FacebookIcon />, label: 'Facebook' },
                      ].map(({ href, icon, label }) => (
                        <IconButton
                          key={label}
                          component="a"
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={label}
                          sx={{
                            backgroundColor: '#eaf4ea',
                            color: '#3d8b40',
                            border: '1px solid #c8e6c9',
                            '&:hover': { backgroundColor: '#eaf4ea', color: 'white', transform: 'translateY(-3px)', boxShadow: '0 6px 20px rgba(61,139,64,0.3)' },
                          }}
                        >
                          {icon}
                        </IconButton>
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ mt: 4 }}>
                    <GreenButton
                      fullWidth
                      component="a"
                      href={companyData.company.contact.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<WhatsAppIcon />}
                    >
                      Direkt auf WhatsApp schreiben
                    </GreenButton>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={7}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#ffffff',
                      borderRadius: 4,
                      p: { xs: 2.5, md: 4 },
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2.5, color: '#1f3d24', fontSize: { xs: '1.15rem', md: '1.35rem' } }}>
                      Kostenlose Besichtigung anfragen
                    </Typography>
                    <form id="contactFormStandard" action="https://formspree.io/f/xykabewd" method="POST" style={{ display: 'grid', gap: { xs: 10, md: 14 } }}>
                      <input type="hidden" name="_subject" value="Neue Anfrage von der Clean-Life Startseite" />
                      <input type="text" name="name" placeholder="Name *" required style={fieldStyle} />
                      <input type="email" name="email" placeholder="E-Mail *" required style={fieldStyle} />
                      <input type="tel" name="phone" placeholder="Telefon" style={fieldStyle} />
                      <input type="text" name="weiterempfehlungscode" placeholder="Weiterempfehlungscode (z.B. FREUNDE100)" style={fieldStyle} />
                      <select name="service" style={fieldStyle}>
                        <option value="">Art der Reinigung</option>
                        <option value="Treppenhausreinigung">Treppenhausreinigung</option>
                        <option value="Fensterreinigung">Fensterreinigung</option>
                        <option value="Unterhaltsreinigung">Unterhaltsreinigung</option>
                        <option value="Büro Unterhaltsreinigung">Büro Unterhaltsreinigung</option>
                        <option value="Winterdienst">Winterdienst</option>
                        <option value="Hausmeisterdienste">Hausmeisterdienste</option>
                      </select>
                      <Button
                        type="submit"
                        fullWidth
                        sx={{
                          background: 'linear-gradient(135deg, #3d8b40 0%, #2e6b30 100%)',
                          color: 'white',
                          py: 1.6,
                          fontWeight: 700,
                          fontSize: { xs: '1rem', md: '1.05rem' },
                          mt: 1,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #2e6b30 0%, #1e4d20 100%)',
                          },
                        }}
                      >
                        Anfrage senden
                      </Button>
                    </form>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </WhiteSection>
      )}

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <Box sx={{ backgroundColor: '#0a0f1e', color: 'white', pt: 8, pb: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/${updateImages.navIcon}`}
                  alt="SAMUSEW"
                  style={{ height: 48, width: 'auto', display: 'block' }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
                  <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1.05rem', letterSpacing: '0.3px' }}>
                    SAMUSEW
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontWeight: 500, fontSize: '0.65rem', letterSpacing: '0.5px', textTransform: 'uppercase', mt: 0.25 }}>
                    Gebäude & Garten Management
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.6, maxWidth: 280, lineHeight: 1.8 }}>
                Professionelles Gebäude- und Gartenmanagement im Rhein-Neckar-Gebiet seit 2023.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                {[
                  { href: companyData.company.contact.whatsapp, icon: <WhatsAppIcon fontSize="small" /> },
                  { href: companyData.company.contact.instagram, icon: <InstagramIcon fontSize="small" /> },
                  { href: companyData.company.contact.facebook, icon: <FacebookIcon fontSize="small" /> },
                ].map(({ href, icon }, i) => (
                  <IconButton
                    key={i}
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'rgba(255,255,255,0.5)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      p: 1,
                      '&:hover': { color: '#3d8b40', borderColor: '#3d8b40' },
                    }}
                  >
                    {icon}
                  </IconButton>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2.5 }}>Kontakt</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="body2" sx={{ opacity: 0.6 }}>{companyData.company.address}</Typography>
                <Link href={`tel:${companyData.company.contact.phone}`} color="inherit" underline="hover" sx={{ opacity: 0.6, fontSize: '0.875rem', '&:hover': { opacity: 1, color: '#3d8b40' } }}>
                  {companyData.company.contact.phone}
                </Link>
                <Link href={`mailto:${companyData.company.contact.email}`} color="inherit" underline="hover" sx={{ opacity: 0.6, fontSize: '0.875rem', '&:hover': { opacity: 1, color: '#3d8b40' } }}>
                  {companyData.company.contact.email}
                </Link>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2.5 }}>Rechtliches</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Link href="/impressum" color="inherit" underline="hover" sx={{ opacity: 0.6, fontSize: '0.875rem', '&:hover': { opacity: 1, color: '#3d8b40' } }}>
                  Impressum
                </Link>
                <Link href="/impressum" color="inherit" underline="hover" sx={{ opacity: 0.6, fontSize: '0.875rem', '&:hover': { opacity: 1, color: '#3d8b40' } }}>
                  Datenschutzerklärung
                </Link>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 5, borderColor: 'rgba(255,255,255,0.08)' }} />

          <Typography variant="body2" align="center" sx={{ opacity: 0.4 }}>
            © {new Date().getFullYear()} {companyData.company.name}. Alle Rechte vorbehalten.
          </Typography>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default LandingPage;
